import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import casinoWS from '../services/websocket';

const LiveBetsContext = createContext(null);

// Dedup using Set — O(n) instead of O(n²)
const dedup = (bets) => {
  const seen = new Set();
  return bets.filter(bet => {
    if (seen.has(bet.id)) return false;
    seen.add(bet.id);
    return true;
  });
};

// Only clone bets whose isNew flag needs to change
const clearIsNew = (prev) => {
  const hasAnyNew = prev.some(b => b.isNew);
  return hasAnyNew ? prev.map(b => b.isNew ? { ...b, isNew: false } : b) : prev;
};

export const LiveBetsProvider = ({ children }) => {
  const [liveBets, setLiveBets] = useState([]);

  useEffect(() => {
    const unsubscribers = [];

    unsubscribers.push(casinoWS.on('init', (data) => {
      if (data.recentBets && Array.isArray(data.recentBets)) {
        setLiveBets(data.recentBets.slice(0, 50).map(bet => ({
          ...bet,
          id: bet.id,
          isNew: false,
        })));
      }
    }));

    unsubscribers.push(casinoWS.on('liveBets', (data) => {
      if (!data.bets || !Array.isArray(data.bets)) return;

      const betsToShowNow = [];

      data.bets.forEach(bet => {
        betsToShowNow.push({ ...bet, id: bet.id, isNew: true });
      });

      if (betsToShowNow.length > 0) {
        setLiveBets(prev => {
          const merged = [...betsToShowNow, ...clearIsNew(prev)];
          return dedup(merged).slice(0, 50);
        });
      }
    }));

    unsubscribers.push(casinoWS.on('betHistory', (data) => {
      if (!data.bets || !Array.isArray(data.bets)) return;

      setLiveBets(prev => {
        const newBets = data.bets.map(bet => ({ ...bet, id: bet.id, isNew: false }));
        const merged = [...newBets, ...prev];
        return dedup(merged).slice(0, 50);
      });

      window.dispatchEvent(new CustomEvent('betHistoryReceived', { detail: data }));
    }));

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  const fetchBetHistory = useCallback((tab, game = null) => {
    casinoWS.send({ type: 'getBetHistory', data: { tab, limit: 50, game } });
  }, []);

  const value = useMemo(() => ({ liveBets, fetchBetHistory }), [liveBets, fetchBetHistory]);

  return (
    <LiveBetsContext.Provider value={value}>
      {children}
    </LiveBetsContext.Provider>
  );
};

export const useLiveBets = () => {
  const context = useContext(LiveBetsContext);
  if (!context) {
    throw new Error('useLiveBets must be used within a LiveBetsProvider');
  }
  return context;
};

export default LiveBetsContext;
